import React, { useContext, useEffect, useRef, useState } from "react";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AppSettings from "../../../Settings/AppSettings";
import AuthManager from "../../../Common/AuthManager";
import { CategoryContext } from "./CategoryContext";
import { AppContext } from "../../../Context/AppContext";
import { toast } from "Components/DesignSystem/Toast/useToast";
import Utils from "../../../Common/Utils";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Breadcrumbs from "Components/DesignSystem/Breadcrumbs/Breadcrumbs";
import Button from "Components/DesignSystem/Button/Button";
import Card from "Components/DesignSystem/Card/Card";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Menu from "Components/DesignSystem/Menu/Menu";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import TextField from "Components/DesignSystem/TextField/TextField";
import Textarea from "Components/DesignSystem/Textarea/Textarea";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Copy, MoreVertical } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";
import { useDismissable } from "Components/DesignSystem/useDismissable";

/**
 * Category Type create/edit form and detail view, on the design system since the
 * category module's Phase 2 — every service call, permission check, validation
 * rule and timer is the legacy logic verbatim; only the rendering changed.
 * Phase 6 moved the two BootBox dialogs (delete confirm, delete-failure
 * notice) onto DS Dialog — the § 76 pattern; the permission gate, its
 * toast.success-on-denial tone quirk (§ 7) and what confirming calls are
 * unchanged.
 */
function AddEditViewCategoryType() {
  let appContext = useContext(AppContext);
  let industryId =
    appContext.get_selected_industry() && appContext.get_selected_industry().id;
  const appData = useContext(AppContext);

  const categoryTypeContextData = useContext(CategoryContext);
  let [loading, setLoading] = React.useState(false);
  let [savingInfo, setsavingInfo] = React.useState({
    done: null,
    message: "",
  });
  let [errorMessage, setErrorMessage] = useState(null);
  let initialStateObject = {
    id: "",
    name: "",
    description: "",
    active: true
  };
  const validationOptions = {
    name: {
      required: true,
      minLength: 2,
      maxLength: 60,
      type: "alnum_with_spaces",
    },
  };
  const [disableButtons, setDisableButtons] = useState(true);
  let [categoryTypeDetail, setCategoryTypeDetail] =
    React.useState(initialStateObject);
  let [categoryTypeDetailCopy, setCategoryTypeDetailCopy] =
    React.useState(null);

  async function load_CategoryType_detail() {
    setLoading(true);
    let api_base_url = AppSettings.GMS_BACKEND_API_URL;
    let res = await ServiceWrapper.doGet(
      api_base_url +
      "categorytype/details/" +
      categoryTypeContextData.editingItem.id,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      await setCategoryTypeDetail(res.data.data[0]);
      await setCategoryTypeDetailCopy(res.data.data[0]);
      setLoading(false);
      setDisableButtons(true);
    } else {
      setLoading(false);
      setErrorMessage(
        "Error while fetching details, Please try after some time."
      );
    }
  }

  async function hande_change(e) {
    let temp = JSON.parse(JSON.stringify(categoryTypeDetail));
    temp[e.target.name] =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCategoryTypeDetail(temp);
  }

  useEffect(() => {
    const validation = Utils.validate(categoryTypeDetail, validationOptions);
    if (
      !validation[0] ||
      JSON.stringify(categoryTypeDetail) ===
      JSON.stringify(categoryTypeDetailCopy)
    ) {
      setDisableButtons(true);
    } else setDisableButtons(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryTypeDetail]);

  async function save_changes() {
    setDisableButtons(true);
    setsavingInfo(null);

    let api_base_url = AppSettings.GMS_BACKEND_API_URL;

    let final_details = categoryTypeDetail;
    let final_url = `${api_base_url}categorytype/edit/${categoryTypeContextData.editingItem.id}`;
    if (
      categoryTypeContextData.editingItem.type === "ct" &&
      categoryTypeContextData.editingItem.id === "0"
    ) {
      final_url = `${api_base_url}categorytype/create`;
      final_details = {
        ...categoryTypeDetail,
        industry_id: industryId,
      };
    }
    let res = await ServiceWrapper.doPost(
      final_url,
      final_details,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        toast.success(res.data.message);
        if (
          categoryTypeContextData.editingItem.type === "ct" &&
          categoryTypeContextData.editingItem.id === "0"
        ) {
          setCategoryTypeDetail(initialStateObject);
          setCategoryTypeDetailCopy(initialStateObject);
          setDisableButtons(true);
        } else {
          setCategoryTypeDetailCopy(categoryTypeDetail);
        }
        if (categoryTypeContextData.editingItem.id !== "0") {
          handle_reload_list(categoryTypeContextData.editingItem.id);
        } else {
          handle_reload_list(res?.data?.id);
        }
      } else {
        toast.error(res.data.message);
        setDisableButtons(false);
      }
    } else {
      setDisableButtons(false);
      setsavingInfo({
        done: false,
        message: "Error while saving, Please try after some time.",
      });
    }
    setDisableButtons(true);
    setTimeout(() => {
      setsavingInfo({
        done: null,
        message: "",
      });
    }, 5000);
  }

  useEffect(() => {
    if (
      JSON.stringify(categoryTypeDetail) ===
      JSON.stringify(initialStateObject) ||
      !categoryTypeDetail.name.trim()
    ) {
      setDisableButtons(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryTypeDetail]);

  function handle_reload_list(id) {
    categoryTypeContextData.setEditingItem({
      type: "ct",
      form: "view",
      id: id,
    });
    if (categoryTypeDetailCopy) {
      if (
        !(
          categoryTypeDetail.name === categoryTypeDetailCopy.name &&
          categoryTypeDetail.active === categoryTypeDetailCopy.active
        )
      ) {
        categoryTypeContextData.setListReloadCounter((n) => n + 1);
      }
    } else {
      categoryTypeContextData.setListReloadCounter((n) => n + 1);
    }
    copy_categoryType_detail();
  }

  function copy_categoryType_detail() {
    let temp = JSON.parse(JSON.stringify(categoryTypeDetail));
    setCategoryTypeDetailCopy(temp);
  }

  useEffect(() => {
    if (categoryTypeContextData.editingItem.form === "edit") {
      if (
        categoryTypeContextData.editingItem.type === "ct" &&
        categoryTypeContextData.editingItem.id !== "0"
      ) {
        load_CategoryType_detail();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryTypeContextData.editingItem]);

  const handleOnSaveClick = () => {
    if (appData.pm.checkPermission("gmscategory_update")) {
      if (categoryTypeDetail?.name.trim() !== "") {
        save_changes();
      } else {
        toast.error("Please enter a valid name");
      }
    } else {
      toast.info("You do not have permission to save the category type");
    }
  }

  const handleOnCancelClick = () => {
    categoryTypeContextData.editingItem.id === "0"
      ? categoryTypeContextData.setEditingItem((prev) => ({ id: categoryTypeContextData.categoryTypeList[0]?.id ?? undefined, type: "ct", form: "view" }))
      : categoryTypeContextData.setEditingItem((prev) => ({ id: prev?.id, type: "ct", form: "view" }));
  }

  return (
    <React.Fragment>
      {categoryTypeContextData.editingItem.form === "view" ? (
        <CategoryTypeDetails />
      ) : (
        <>
          {loading && (
            <div className="tw-space-y-4">
              <Skeleton variant="rect" width="25%" height="20px" />
              <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-4 tw-space-y-4">
                <div className="tw-flex tw-justify-between">
                  <Skeleton variant="rect" width="30%" height="24px" />
                  <Skeleton variant="rect" width="15%" height="24px" />
                </div>
                <Skeleton variant="rect" width="100%" height="36px" />
                <Skeleton variant="rect" width="100%" height="96px" />
              </div>
            </div>
          )}
          {!loading &&
            (errorMessage ? (
              <Alert tone="danger" live data-testid="category-type-form-error-alert">
                {errorMessage}
              </Alert>
            ) : (
              <>
                <div className="mt-1">
                  <Breadcrumbs
                    className="tw-ms-3.5 tw-my-2"
                    data-testid="category-type-form-breadcrumbs"
                    items={[
                      /* Non-navigable waypoint — the legacy crumb was a fake
                         `to="#"` link under pointer-events-none (§ 69 grammar). */
                      { label: "Taxonomy" },
                      { label: categoryTypeDetail ? categoryTypeDetail?.name : "" },
                    ]}
                  />
                </div>
                <Card className="tw-m-1">
                  <div className="d-flex justify-content-between">
                    <div>
                      <Typography variant="heading-2" as="h2" className="tw-text-ink">
                        {`${categoryTypeContextData.editingItem.id === "0"
                          ? "Create "
                          : "Edit "
                          } Category Type`}
                      </Typography>
                      {categoryTypeContextData.editingItem.id !== "0" && (
                        <Typography variant="caption-metadata" as="p" className="tw-text-ink-secondary tw-mt-1">
                          {`UID: ${categoryTypeContextData.editingItem.id !== "0"
                            ? categoryTypeContextData.editingItem.id
                            : ""
                            }`}
                        </Typography>
                      )}
                    </div>

                    <div className="tw-flex tw-gap-3 tw-items-start float-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleOnCancelClick}
                        data-testid="category-type-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={disableButtons}
                        onClick={handleOnSaveClick}
                        data-testid="category-type-save-btn"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                  <form className="tw-mt-4">
                    <div className="mb-3">
                      <TextField
                        label="Name"
                        id="name"
                        name="name"
                        data-testid="category-addeditviewcategorytype-input-1"
                        placeholder="Enter Category Type Name"
                        value={categoryTypeDetail.name || ""}
                        /* hande_change keys off event.target.name/.type — hand it
                           the event, exactly as the native input did. */
                        onChange={(_, e) => hande_change(e)}
                        maxLength={60}
                        minLength={2}
                        error={!(categoryTypeDetail?.name?.trim().length >= 2)}
                        helperText="Name must be 2-60 characters long, contain letters, numbers and spaces, and must not contain special characters."
                      />
                    </div>

                    <div className="mb-3">
                      <Textarea
                        label="Description"
                        id="description"
                        name="description"
                        data-testid="category-addeditviewcategorytype-textarea-2"
                        rows={5}
                        placeholder="Enter description"
                        onChange={(_, e) => hande_change(e)}
                        value={categoryTypeDetail.description || ""}
                      />
                    </div>
                    <div className="float-start">
                      {/* The legacy strip's reachable states, on the § 99 grammar:
                          savingInfo === null while a save is in flight ("Saving your
                          Changes"), done === false on failure. The done === true
                          branch is kept although the current save path only toasts. */}
                      {savingInfo !== null ? (
                        <>
                          {savingInfo.done ? (
                            <Alert tone="success" live data-testid="category-type-save-status-alert">
                              {savingInfo.message}
                            </Alert>
                          ) : (
                            <>
                              {savingInfo.done === false && (
                                <Alert tone="danger" live data-testid="category-type-save-status-alert">
                                  {savingInfo.message}
                                </Alert>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <Alert tone="info" live data-testid="category-type-save-status-alert">
                          <span className="tw-inline-flex tw-items-center tw-gap-2">
                            <Spinner size="sm" aria-hidden="true" />
                            <span>Saving your Changes</span>
                          </span>
                        </Alert>
                      )}
                    </div>
                  </form>
                </Card>
              </>
            ))}
        </>
      )}
    </React.Fragment>
  );
}

export default AddEditViewCategoryType;

const CategoryTypeDetails = () => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const { editingItem, setEditingItem, setListReloadCounter, setDeleteInfo } =
    useContext(CategoryContext);
  const appData = useContext(AppContext);

  let [loading, setLoading] = React.useState({
    status: "",
    message: "",
  });

  let [categoryTypeDetail, setCategoryTypeDetail] = React.useState();

  const [clipText, setClipText] = useState(null);

  /* The kebab dropdown (was a Bootstrap dropdown-menu) — § 76 pattern. */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapperRef = useRef(null);
  useDismissable({
    isOpen: menuOpen,
    onDismiss: () => setMenuOpen(false),
    isInside: (target) => !!menuWrapperRef.current?.contains(target),
  });
  /* Phase 6 (was BootBox): the delete confirm and the delete-failure notice. */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState(null);

  /* "UID Copied" dismisses itself after 2s (user-requested 2026-09-01 — it
     used to stay until the component unmounted). The ref resets the timer on
     a repeat click; the effect clears it on unmount. */
  const clipTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(clipTimerRef.current), []);

  function handle_clipbord(text) {
    navigator.clipboard.writeText(text).then(() => {
      setClipText(text);
      clearTimeout(clipTimerRef.current);
      clipTimerRef.current = setTimeout(() => setClipText(null), 2000);
    });
  }

  useEffect(() => {
    load_CategoryType_detail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem.id]);

  async function load_CategoryType_detail() {
    setLoading({ status: "loading", message: "Loading Category Type Details" });
    let res = await ServiceWrapper.doGet(
      api_base_url + "categorytype/details/" + editingItem.id,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      setLoading({ status: "success", message: "" });
      await setCategoryTypeDetail(res.data.data[0]);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  async function handle_delete_categoryType(categoryType) {
    setDeleteInfo({
      status: "Loading",
      message: "Deleting selected Category Type",
    });

    let res = await ServiceWrapper.doGet(
      api_base_url + "categorytype/delete/" + categoryType.id,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        toast.success(res.data.message);
        if (
          editingItem !== null &&
          editingItem !== undefined &&
          editingItem.type === "ct" &&
          editingItem.id === categoryType.id
        ) {
          setEditingItem(null);
        }
        setListReloadCounter((n) => n + 1);
      } else {
        setDeleteInfo({
          status: "Error",
          message: "Deletion of Category type Failed",
        });

        setDeleteFailure(res.data.message);
      }
    } else {
      setDeleteInfo({
        status: "Error",
        message: "Error while deleting selected category type!!",
      });
    }
    setTimeout(() => {
      setDeleteInfo({
        status: null,
        message: "",
      });
    }, 5000);
  }

  return (
    <>
      {/* Delete confirm (was Bootbox.confirm with an empty title):
          same legacy copy in the body, same callback —
          handle_delete_categoryType(categoryTypeDetail). */}
      <Dialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete category type?"
        tone="danger"
        data-testid="category-type-delete-confirm-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              data-testid="category-type-delete-cancel-btn"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger-primary"
              data-testid="category-type-delete-confirm-btn"
              onClick={() => {
                setDeleteConfirmOpen(false);
                handle_delete_categoryType(categoryTypeDetail);
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          Do you really want to delete{" "}
          <strong>{categoryTypeDetail?.name}</strong>?
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink-secondary">
          This cannot be undone.
        </Typography>
      </Dialog>
      {/* Delete-failure notice (was a BootBox acknowledge dialog). */}
      <Dialog
        isOpen={deleteFailure !== null}
        onClose={() => setDeleteFailure(null)}
        title="Category Type Deletion Failure"
        tone="danger"
        showCloseButton={false}
        data-testid="category-type-delete-failure-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="primary"
              data-testid="category-type-delete-failure-ok-btn"
              onClick={() => setDeleteFailure(null)}
            >
              OK, I got it
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          We are Sorry, We could not delete the selected Category Type Below is
          more information.
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink">
          <strong>{deleteFailure}</strong>
        </Typography>
      </Dialog>
      <Breadcrumbs
        className="tw-ms-2 tw-mt-3 tw-mb-2"
        data-testid="category-type-detail-breadcrumbs"
        items={[
          { label: "Taxonomy" },
          {
            label:
              loading.status === "success" && categoryTypeDetail
                ? categoryTypeDetail?.name
                : "",
          },
        ]}
      />

      {loading.status === "loading" && (
        <div className="tw-ms-1 tw-border tw-border-edge tw-rounded-ctl tw-p-4 tw-space-y-3">
          <Skeleton variant="rect" width="20%" height="24px" />
          <Skeleton variant="rect" width="40%" height="16px" />
          <Skeleton variant="rect" width="20%" height="16px" />
        </div>
      )}

      {loading.status === "Error" && (
        /* Legacy painted this error as bootstrap alert-primary (blue) — the § 99
           fix applies here too: an error wears the danger tone. */
        <Alert tone="danger" live data-testid="category-type-detail-error-alert">
          {loading.message}
        </Alert>
      )}
      {loading.status === "success" && categoryTypeDetail && (
        <Card className="tw-ml-2">
          <div className="d-flex justify-content-between ps-0 pb-0">
            <Typography variant="heading-2" as="h2" className="tw-text-ink">
              {categoryTypeDetail.name}
            </Typography>

            <div className="tw-flex tw-items-start tw-gap-2">
              <Button
                variant="secondary"
                size="sm"
                data-testid="category-type-edit-btn"
                onClick={() => {
                  if (appData.pm.checkPermission("gmscategory_update")) {
                    setEditingItem({
                      type: "ct",
                      form: "edit",
                      id: categoryTypeDetail.id,
                    });
                  } else {
                    toast.success(
                      "You don't have permission to update the category type"
                    );
                  }
                }}
              >
                Edit
              </Button>
              <div ref={menuWrapperRef} className="tw-relative">
                <button
                  type="button"
                  id="btnGroupDrop1"
                  data-testid="category-addeditviewcategorytype-i-1"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="More actions"
                  onClick={() => setMenuOpen((open) => !open)}
                  className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-ink-secondary hover:tw-text-ink hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
                >
                  <MoreVertical size={16} aria-hidden="true" />
                </button>
                {menuOpen && (
                  <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-36">
                    <Menu
                      variant="compact"
                      data-testid="category-type-overflow-menu"
                      items={[
                        {
                          id: "delete",
                          label: "Delete",
                          tone: "danger",
                          "data-testid": "category-type-delete-menu-item",
                          onClick: () => {
                            setMenuOpen(false);
                            if (
                              appData.pm.checkPermission("gmscategory_delete")
                            ) {
                              setDeleteConfirmOpen(true);
                            } else {
                              toast.success(
                                "You don't have permission to delete the category type"
                              );
                            }
                          },
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col col-md-12">
            <p className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
              <Typography variant="title" as="span" className="tw-text-ink">
                UID
              </Typography>
              <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                {`${categoryTypeDetail.id || ""}`}
              </Typography>
              <button
                type="button"
                data-testid="category-addeditviewcategorytype-i-2"
                aria-label="Copy UID"
                onClick={() => handle_clipbord(categoryTypeDetail.id)}
                className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-accent hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
              >
                <Copy size={14} aria-hidden="true" />
              </button>
              {clipText !== null && (
                <Typography variant="caption-metadata" as="span" className="tw-text-tone-success-ink">
                  UID Copied
                </Typography>
              )}
            </p>
            <div className="ps-0 mt-0">
              <Typography variant="body-primary" as="p" className="tw-text-ink">
                {categoryTypeDetail.description}
              </Typography>
            </div>
            <br />
          </div>
        </Card>
      )}
    </>
  );
};
